import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadFull } from "tsparticles";
import Particles from "react-tsparticles";
import { motion } from "framer-motion";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const HeroSection = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [documentText, setDocumentText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [modelResponses, setModelResponses] = useState({
    modelA: {
      name: "LawSutra.AI",
      response: ""
    },
    modelB: {
      name: "OpenAI",
      response: ""
    }
  });

  useEffect(() => {
    setIsVisible(true);
  }, []);
  
  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('/assets/Confidentiality–and-Non-disclosure-Agreement.txt');
        
        if (!response.ok) {
          throw new Error(`Failed to load document: ${response.status} ${response.statusText}`);
        }
        
        const text = await response.text();
        setDocumentText(text);
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading document:", error);
        setLoadError(error.message);
        setIsLoading(false);
      }
    };

    fetchDocument();
  }, []);

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const lawsutraResponse = await fetch('/assets/lawsutra-nda-response.txt');
        const openaiResponse = await fetch('/assets/chatgpt-nda-response.txt');

        if (!lawsutraResponse.ok || !openaiResponse.ok) {
          throw new Error('Failed to load one or more responses');
        }

        const lawsutraText = await lawsutraResponse.text();
        const openaiText = await openaiResponse.text();

        setModelResponses(prevResponses => ({
          ...prevResponses,
          modelA: { ...prevResponses.modelA, response: lawsutraText },
          modelB: { ...prevResponses.modelB, response: openaiText }
        }));

        console.log(modelResponses);
        console.log(lawsutraText);
      } catch (error) {
        console.error("Error loading responses:", error);
      }
    };

    fetchResponses();
  }, []);
  const particlesInit = useCallback(async (engine) => {
    await loadFull(engine);
  }, []);

  const particlesConfig = {
    particles: {
      number: {
        value: 80,
        density: { enable: true, value_area: 800 }
      },
      color: { 
        value: ["#4facfe", "#00f2fe", "#a8edea", "#fed6e3"]
      },
      shape: {
        type: "circle"
      },
      opacity: {
        value: 0.6, // Increased base opacity
        random: true,
        anim: {
          enable: true,
          speed: 1, // Slightly faster opacity animation
          opacity_min: 0.2,
          sync: false
        }
      },
      size: {
        value: 5, // Slightly larger particles
        random: true,
        anim: {
          enable: true,
          speed: 3, // Faster size animation
          size_min: 0.5,
          sync: false
        }
      },
      line_linked: {
        enable: true,
        distance: 180, // Increased connection distance
        color: "#4facfe",
        opacity: 0.4, // Slightly more visible connections
        width: 1.2 // Slightly thicker lines
      },
      move: {
        enable: true,
        speed: 2,
        direction: "none",
        random: true,
        straight: false,
        out_mode: "bounce",
        bounce: true,
        attract: {
          enable: true,
          rotateX: 800,
          rotateY: 1500
        }
      }
    },
    interactivity: {
      detect_on: "canvas",
      events: {
        onhover: {
          enable: true,
          mode: "bubble" // Changed to bubble for more interesting interaction
        },
        onclick: {
          enable: true,
          mode: "push"
        },
        resize: true
      },
      modes: {
        bubble: {
          distance: 250,
          size: 12,
          duration: 2,
          opacity: 0.8,
          speed: 3
        },
        push: {
          particles_nb: 6 // Increased particles added on click
        }
      }
    },
    background: {
      color: "#000428",
      image: "linear-gradient(135deg, #000428 0%, #004e92 100%)",
      position: "50% 50%",
      repeat: "no-repeat",
      size: "cover"
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.4
      }
    }
  };

  const fadeUpVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 15,
        stiffness: 100
      }
    }
  };

  const floatingAnimation = {
    y: [0, -10, 0],
    transition: {
      duration: 5,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  // New animation variants for the demo section
  const demoSectionVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delay: 0.5,
        duration: 0.8
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 80
      }
    }
  };

  // Add this function to handle the demo button click
  const handleDemoClick = () => {
    // Update URL without full page reload
    window.history.pushState({}, '', '/demo');
    // Smooth scroll to demo section
    document.getElementById('demo-section').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <div className="hero-section relative min-h-screen" style={{
        background: "linear-gradient(135deg, #000428 0%, #004e92 100%)",
        position: "relative",
      }}>
        <Particles
          id="tsparticles"
          init={particlesInit}
          options={particlesConfig}
          className="particles-background"
          style={{
            position: "absolute",
            zIndex: 0,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        />

        <div 
          className="hero-content"
          style={{ 
            gap: "2rem", 
            display: "flex", 
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            maxWidth: "800px",
            margin: "6rem auto 0",
            padding: "0 2rem",
            minHeight: "calc(100vh - 10rem)",
            position: "relative",
            zIndex: 1
          }}
        >
          <h1
            className="gradient-text"
          >
            Your Intelligent Legal Ally
          </h1>

          <p
            className="hero-subtitle"
            style={{ marginBottom: "1rem" }}
          >
            Empowering legal professionals with cutting-edge AI to streamline research, manage cases, and redefine the future of law.
          </p>

          <div 
            className="hero-buttons"
            style={{ 
              gap: "0.5rem",
              display: "flex",
              justifyContent: "center",
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              width: "100%",
              maxWidth: "800px",
              margin: "0 auto"
            }}
          >
            <button
              onClick={() => window.open('https://chat.lawsutra.ai', '_blank')}
              className="primary-button"
              style={{ 
                width: "calc(100% - 0.5rem)",
                maxWidth: "200px",
                height: "50px",
                margin: "0.25rem"
              }}
            >
              Try for FREE!
            </button>

            <button
              onClick={() => window.open('https://blog.lawsutra.ai', '_blank')}
              className="primary-button"
              style={{ 
                width: "calc(100% - 0.5rem)",
                maxWidth: "200px",
                height: "50px",
                margin: "0.25rem",
                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                boxShadow: "0 4px 15px rgba(79, 172, 254, 0.4)",
                border: "none",
                borderRadius: "8px",
                fontWeight: "600",
                letterSpacing: "0.5px",
                transition: "all 0.3s ease",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "8px"
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
              onMouseOut={(e) => e.currentTarget.style.transform = "translateY(0)"}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="white"/>
              </svg>
              Read Blog
            </button>

            <button
              onClick={handleDemoClick}
              className="primary-button"
              style={{ 
                width: "calc(100% - 0.5rem)",
                maxWidth: "200px",
                height: "50px",
                margin: "0.25rem"
              }}
            >
              See Demo
            </button>
          </div>
        </div>
      </div>

      {/* Partners Section - Simple Version */}
      <section
        className="partners-section"
        style={{
          padding: "3rem 1rem 4rem",
          background: "linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 78, 146, 0.2))",
          position: "relative",
          zIndex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          marginTop: "2rem",
          marginBottom: "2rem"
        }}
      >
        <div 
          style={{
            width: "100%",
            maxWidth: "1500px",
            margin: "0 auto"
          }}
        >
          <div
            style={{
              background: "#FFFFFF",
              borderRadius: "16px",
              padding: "3rem 2rem",
              border: "1px solid rgba(220, 220, 220, 0.5)",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
              position: "relative"
            }}
          >
            {/* Simple text inside white box */}
            <p style={{
              color: "#333",
              fontSize: "1.25rem",
              fontWeight: "500",
              textAlign: "center",
              marginBottom: "2rem",
              maxWidth: "800px",
              margin: "0 auto 2rem"
            }}>
              LawSutra.AI is honored to be supported by the world's top technology leaders
            </p>
            
            {/* Simple horizontal partner logos */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                overflowX: "auto",
                paddingBottom: "20px",
                msOverflowStyle: "none",
                scrollbarWidth: "none"
              }}
              className="hide-scrollbar"
            >
              <div style={{ 
                display: "flex", 
                flexWrap: "nowrap",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "2.5rem",
                padding: "0 rem",
                minWidth: "fit-content"
              }}>
                {[
                  {
                    name: "NVIDIA Inception",
                    logo: "/assets/partners/nvidia-inception.svg",
                    width: "200px",
                    padding: "10px"
                  },
                  {
                    name: "Amazon AWS",
                    logo: "/assets/partners/aws.svg",
                    width: "250px",
                    padding: "10px"
                  },
                  {
                    name: "Microsoft Azure",
                    logo: "/assets/partners/azure.svg",
                    width: "210px",
                    padding: "10px"
                  },
                  {
                    name: "Google Cloud",
                    logo: "/assets/partners/gcp.svg",
                    width: "500px",
                    padding: "10px"
                  },
                  {
                    name: "Oracle",
                    logo: "/assets/partners/oracle.svg",
                    width: "200px",
                    padding: "10px"
                  },
                  {
                    name: "Cloudflare",
                    logo: "/assets/partners/cloudflare.svg",
                    width: "200px",
                    padding: "12px"
                  }
                ].map((partner, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      width: "200px"
                    }}
                  >
                    <div
                      style={{
                        width: "160px",
                        height: "160px",
                        marginBottom: "1rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#FFFFFF",
                        borderRadius: "50%",
                        padding: partner.padding || "10px",
                        boxSizing: "border-box",
                      }}
                    >
                      <img 
                        src={partner.logo} 
                        alt={`${partner.name} logo`} 
                        style={{ 
                          width: partner.width || "90px",
                          maxHeight: "130px",
                          objectFit: "contain",
                          opacity: 1
                        }} 
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Add CSS for hiding scrollbar */}
            <style jsx global>{`
              .hide-scrollbar::-webkit-scrollbar {
                display: none;
              }
              
              .hide-scrollbar {
                -ms-overflow-style: none;
                scrollbar-width: none;
              }
            `}</style>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section
        id="demo-section"
        className="demo-section"
        style={{
          padding: "4rem 1rem",
          background: "transparent",
          position: "relative",
          zIndex: 1,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center"
        }}
      >
        <div className="container mx-auto" style={{ 
          maxWidth: "1400px",
          width: "100%",
          padding: "0 0.5rem"
        }}>
          <div style={{ 
            display: "flex", 
            flexDirection: "column",
            gap: "1.5rem",
            alignItems: "start",
            background: "rgba(255, 255, 255, 0.05)",
            padding: "1.5rem 1rem",
            borderRadius: "16px",
            border: "1px solid rgba(79, 172, 254, 0.3)",
            backdropFilter: "blur(10px)"
          }}>
            <h2 
              className="text-3xl md:text-4xl font-bold"
              style={{
                color: "#4facfe",
                width: "100%",
                textAlign: "center",
                marginBottom: "0.5rem",
                fontSize: "clamp(1.75rem, 5vw, 2.5rem)"
              }}
            >
              Why Choose LawSutra.AI?
            </h2>

            <div
              style={{
                background: "rgba(79, 172, 254, 0.1)",
                backdropFilter: "blur(10px)",
                borderRadius: "16px",
                padding: "1.5rem",
                width: "100%",
                border: "1px solid rgba(79, 172, 254, 0.3)"
              }}
            >
              <h3 style={{ 
                color: "#4facfe", 
                marginBottom: "1rem",
                fontSize: "1.2rem",
                fontWeight: "bold"
              }}>
                Contract Review Comparison
              </h3>
              <p style={{ 
                color: "#f0f0f0",
                lineHeight: "1.6",
                marginBottom: "1rem"
              }}>
                In this demonstration, we compare how LawSutra.AI and OpenAI analyze a standard Non-Disclosure Agreement (NDA). Our specialized legal AI provides structured, legally-focused insights, while OpenAI offers a more general analysis.
              </p>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: "0.9rem",
                color: "#cccccc"
              }}>
                <span>Source Document: </span>
                <a
                  href="https://www.sec.gov/Archives/edgar/data/1065246/000119312509046323/dex99e8.htm"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#4facfe",
                    textDecoration: "underline"
                  }}
                >
                  View Original NDA ↗
                </a>
              </div>
            </div>

            <div
              className="models-comparison-container"
              style={{ width: "100%" }}
            >
              <div 
                className="comparison-wrapper"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: "1rem",
                  minHeight: "auto"
                }}
              >
                <div
                  className="model-response"
                  style={{
                    background: "rgba(79, 172, 254, 0.1)",
                    backdropFilter: "blur(10px)",
                    borderRadius: "12px",
                    padding: "1.25rem",
                    border: "1px solid rgba(79, 172, 254, 0.3)",
                    height: "100%",
                    boxShadow: "0 0 15px rgba(79, 172, 254, 0.2), inset 0 0 20px rgba(79, 172, 254, 0.1)"
                  }}
                >
                  <div className="model-header" style={{ marginBottom: "1.5rem" }}>
                    <h4 className="text-xl font-bold" style={{ color: "#4facfe" }}>
                      {modelResponses.modelA.name}
                    </h4>
                    <div className="model-badge" style={{ 
                      display: "inline-block",
                      background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                      padding: "0.25rem 0.75rem",
                      borderRadius: "20px",
                      fontSize: "0.75rem",
                      marginTop: "0.5rem",
                      boxShadow: "0 0 10px rgba(79, 172, 254, 0.4)"
                    }}>
                      Contract Reviewer Base
                    </div>
                  </div>
                  <div className="response-content" style={{ 
                    color: "#f0f0f0",
                    lineHeight: "1.6",
                    fontSize: "0.85rem",
                    maxHeight: "400px",
                    overflow: "auto",
                    paddingRight: "0.5rem",
                    WebkitOverflowScrolling: "touch"
                  }}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h1: ({node, ...props}) => <h1 style={{
                          fontSize: '1.8em',
                          fontWeight: 'bold',
                          color: '#4facfe',
                          marginBottom: '1em',
                          borderBottom: '1px solid rgba(79,172,254,0.3)',
                          paddingBottom: '0.3em'
                        }} {...props} />,
                        h2: ({node, ...props}) => <h2 style={{
                          fontSize: '1.5em',
                          fontWeight: 'bold',
                          color: '#4facfe',
                          marginTop: '1.5em',
                          marginBottom: '0.8em'
                        }} {...props} />,
                        h3: ({node, ...props}) => <h3 style={{
                          fontSize: '1.2em',
                          fontWeight: 'bold',
                          color: '#a8edea',
                          marginTop: '1.2em',
                          marginBottom: '0.6em'
                        }} {...props} />,
                        strong: ({node, ...props}) => <strong style={{
                          color: '#fed6e3',
                          fontWeight: '600'
                        }} {...props} />,
                        blockquote: ({node, ...props}) => <blockquote style={{
                          borderLeft: '3px solid #4facfe',
                          paddingLeft: '1em',
                          marginLeft: '0',
                          color: '#a8edea',
                          fontStyle: 'italic',
                          margin: '1em 0'
                        }} {...props} />,
                        ul: ({node, ...props}) => <ul style={{
                          paddingLeft: '1.5em',
                          marginBottom: '1em'
                        }} {...props} />,
                        ol: ({node, ...props}) => <ol style={{
                          paddingLeft: '1.5em',
                          marginBottom: '1em'
                        }} {...props} />,
                        li: ({node, ...props}) => <li style={{
                          marginBottom: '0.5em'
                        }} {...props} />
                      }}
                    >
                      {modelResponses.modelA.response}
                    </ReactMarkdown>
                  </div>
                </div>

                <div
                  className="model-response"
                  style={{
                    background: "rgba(255, 255, 255, 0.05)",
                    backdropFilter: "blur(10px)",
                    borderRadius: "12px",
                    padding: "1.25rem",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    height: "100%"
                  }}
                >
                  <div className="model-header" style={{ marginBottom: "1.5rem" }}>
                    <h4 className="text-xl font-bold" style={{ color: "#cccccc" }}>
                      {modelResponses.modelB.name}
                    </h4>
                    <div className="model-badge" style={{ 
                      display: "inline-block",
                      background: "#cccccc",
                      color: "#333333",
                      padding: "0.25rem 0.75rem",
                      borderRadius: "20px",
                      fontSize: "0.75rem",
                      marginTop: "0.5rem"
                    }}>
                      ChatGPT
                    </div>
                  </div>
                  <div className="response-content" style={{ 
                    color: "#f0f0f0",
                    lineHeight: "1.6",
                    fontSize: "0.85rem",
                    maxHeight: "400px",
                    overflow: "auto",
                    paddingRight: "0.5rem",
                    WebkitOverflowScrolling: "touch"
                  }}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h1: ({node, ...props}) => <h1 style={{
                          fontSize: '1.8em',
                          fontWeight: 'bold',
                          color: '#4facfe',
                          marginBottom: '1em',
                          borderBottom: '1px solid rgba(79,172,254,0.3)',
                          paddingBottom: '0.3em'
                        }} {...props} />,
                        h2: ({node, ...props}) => <h2 style={{
                          fontSize: '1.5em',
                          fontWeight: 'bold',
                          color: '#4facfe',
                          marginTop: '1.5em',
                          marginBottom: '0.8em'
                        }} {...props} />,
                        h3: ({node, ...props}) => <h3 style={{
                          fontSize: '1.2em',
                          fontWeight: 'bold',
                          color: '#a8edea',
                          marginTop: '1.2em',
                          marginBottom: '0.6em'
                        }} {...props} />,
                        strong: ({node, ...props}) => <strong style={{
                          color: '#fed6e3',
                          fontWeight: '600'
                        }} {...props} />,
                        blockquote: ({node, ...props}) => <blockquote style={{
                          borderLeft: '3px solid #4facfe',
                          paddingLeft: '1em',
                          marginLeft: '0',
                          color: '#a8edea',
                          fontStyle: 'italic',
                          margin: '1em 0'
                        }} {...props} />,
                        ul: ({node, ...props}) => <ul style={{
                          paddingLeft: '1.5em',
                          marginBottom: '1em'
                        }} {...props} />,
                        ol: ({node, ...props}) => <ol style={{
                          paddingLeft: '1.5em',
                          marginBottom: '1em'
                        }} {...props} />,
                        li: ({node, ...props}) => <li style={{
                          marginBottom: '0.5em'
                        }} {...props} />
                      }}
                    >
                      {modelResponses.modelB.response}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-12" style={{ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            padding: '3rem 0'
          }}>
            <button
              onClick={() => window.open('https://chat.lawsutra.ai', '_blank')}
              className="primary-button text-lg"
              style={{
                padding: '1rem 2.5rem',
                fontSize: '1.25rem',
                marginTop: '2rem',
                marginBottom: '2rem'
              }}
            >
              Try for yourself!
            </button>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            flexDirection: 'column',
            width: '100%',
            padding: "0 0.5rem"
          }}>
            <div
              className="methodology-comparison"
              style={{
                marginTop: "2rem",
                padding: "1rem 0.75rem",
                background: "rgba(255, 255, 255, 0.05)",
                backdropFilter: "blur(10px)",
                borderRadius: "16px",
                border: "1px solid rgba(79, 172, 254, 0.3)",
                width: '100%',
                maxWidth: '1200px',
                margin: "2rem auto"
              }}
            >
              <h3 className="text-2xl font-bold mb-6" style={{ 
                color: "#4facfe", 
                textAlign: "center", 
                marginBottom: "1.5rem",
                fontSize: "clamp(1.5rem, 4vw, 2rem)"
              }}>
                LawSutra.AI vs. OpenAI
              </h3>
              
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "1.5rem",
                alignItems: "start"
              }}>
                <div
                  style={{
                    background: "rgba(79, 172, 254, 0.1)",
                    padding: "1.5rem",
                    borderRadius: "12px",
                    border: "1px solid rgba(79, 172, 254, 0.3)"
                  }}
                >
                  <h4 style={{ color: "#4facfe", fontWeight: "bold", marginBottom: "1rem" }}>
                    LawSutra.AI Contract Reviewer
                  </h4>
                  <div className="steps-container" style={{ color: "#e0e0e0" }}>
                    {[
                      { icon: "📋", title: "Thorough Document Check", desc: "Identify type, purpose, and key metadata with precision" },
                      { icon: "👥", title: "Detailed Party Analysis", desc: "Extract and clarify roles of all parties and entities" },
                      { icon: "🔍", title: "Smart Clause Review", desc: "Categorize clauses and formulate guiding questions" },
                      { icon: "⚖️", title: "Risk and Compliance Check", desc: "Identify high-risk clauses and ensure compliance" },
                      { icon: "📊", title: "Insightful Report", desc: "Actionable insights and recommendations" },
                      { icon: "🇮🇳", title: "Indian Legal System Fine-tuning", desc: "Tailored to the Indian legal framework" }
                    ].map((step, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "1rem",
                          padding: "0.5rem",
                          background: "rgba(255, 255, 255, 0.05)",
                          borderRadius: "8px"
                        }}
                      >
                        <span style={{ fontSize: "1.5rem", marginRight: "1rem" }}>{step.icon}</span>
                        <div>
                          <div style={{ fontWeight: "bold", color: "#a8edea" }}>{step.title}</div>
                          <div style={{ fontSize: "0.9rem" }}>{step.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="divider" style={{
                  width: "2px",
                  height: "100%",
                  background: "linear-gradient(to bottom, transparent, #4facfe, transparent)",
                  margin: "0 auto",
                  display: "none",
                  "@media (min-width: 768px)": {
                    display: "block"
                  }
                }} />

                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.05)",
                    padding: "1.5rem",
                    borderRadius: "12px",
                    border: "1px solid rgba(255, 255, 255, 0.1)"
                  }}
                >
                  <h4 style={{ color: "#cccccc", fontWeight: "bold", marginBottom: "1rem" }}>
                    Generic AI Model
                  </h4>
                  <div className="steps-container" style={{ color: "#cccccc" }}>
                    {[
                      { icon: "💭", title: "General Summary", desc: "Basic document overview" },
                      { icon: "📝", title: "Simple Analysis", desc: "Surface-level review" },
                      { icon: "❓", title: "Limited Context", desc: "Missing industry-specific insights" },
                      { icon: "⚠️", title: "Incomplete Review", desc: "May miss critical legal nuances" },
                      { icon: "🔍", title: "Lack of Legal Specialization", desc: "Does not cater to specific legal fields" },
                      { icon: "🇮🇳", title: "Lack of Indian Legal Context", desc: "Not tailored to Indian legal system and regulations" }
                    ].map((step, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "1rem",
                          padding: "0.5rem",
                          background: "rgba(255, 255, 255, 0.05)",
                          borderRadius: "8px",
                          opacity: 0.7
                        }}
                      >
                        <span style={{ fontSize: "1.5rem", marginRight: "1rem" }}>{step.icon}</span>
                        <div>
                          <div style={{ fontWeight: "bold" }}>{step.title}</div>
                          <div style={{ fontSize: "0.9rem" }}>{step.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Add a global style to ensure the background extends to the full page */}
      <style jsx global>{`
        body {
          margin: 0;
          padding: 0;
          min-height: 100vh;
          background: linear-gradient(135deg, #000428 0%, #004e92 100%);
        }

        @media (max-width: 768px) {
          .hero-content {
            padding: 0 1rem;
            margin-top: 4rem;
          }

          .hero-section {
            min-height: auto;
            padding: 2rem 0;
          }

          .demo-section {
            padding: 2rem 0.5rem !important;
          }

          .model-response {
            padding: 1rem !important;
          }
          
          /* Fix for model response background colors on mobile */
          .model-response:first-child {
            background: rgba(79, 172, 254, 0.15) !important;
            border: 1px solid rgba(79, 172, 254, 0.4) !important;
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2) !important;
          }
          
          .model-response:last-child {
            background: rgba(255, 255, 255, 0.08) !important;
            border: 1px solid rgba(255, 255, 255, 0.15) !important;
          }
          
          /* Fix for Contract Review Comparison section on mobile */
          .demo-section .container > div > div:nth-child(2) {
            background: rgba(79, 172, 254, 0.15) !important;
            border: 1px solid rgba(79, 172, 254, 0.4) !important;
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2) !important;
            backdrop-filter: none !important;
            padding: 1rem !important;
          }
          
          /* Disable backdrop-filter on mobile for better performance */
          .model-response {
            backdrop-filter: none !important;
          }
          
          .comparison-wrapper {
            grid-template-columns: 1fr !important;
          }

          .methodology-card {
            padding: 1rem !important;
          }
          
          .steps-container > div {
            padding: 0.5rem 0.25rem !important;
          }
          
          .steps-container span {
            font-size: 1.25rem !important;
            margin-right: 0.5rem !important;
          }
          
          h1 {
            font-size: 2rem !important;
          }

          h2 {
            font-size: 1.75rem !important;
          }

          h3 {
            font-size: 1.5rem !important;
          }

          .response-content {
            font-size: 0.8rem !important;
            max-height: 300px !important;
          }
          
          .model-header {
            margin-bottom: 1rem !important;
          }
          
          .model-badge {
            font-size: 0.7rem !important;
            padding: 0.2rem 0.5rem !important;
          }
        }

        /* Ensure proper scrolling on mobile */
        html, body {
          overflow-x: hidden;
          width: 100%;
          -webkit-overflow-scrolling: touch;
        }

        /* Improve touch interactions */
        * {
          -webkit-tap-highlight-color: transparent;
        }

        /* Improve readability on mobile */
        p, li {
          font-size: 16px;
          line-height: 1.6;
        }

        /* Ensure buttons are easily tappable */
        button {
          min-height: 44px;
          min-width: 44px;
        }
        
        /* Fix overflow issues on small screens */
        .demo-section .container {
          overflow-x: hidden;
        }
        
        /* Improve scrolling in response containers */
        .response-content::-webkit-scrollbar {
          width: 4px;
        }
        
        .response-content::-webkit-scrollbar-thumb {
          background-color: rgba(79, 172, 254, 0.5);
          border-radius: 4px;
        }
        
        .response-content::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.1);
        }
        
        /* Fix margin collapse on mobile */
        .methodology-comparison {
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

export default HeroSection;
